import { useCallback, useEffect, useRef, useState } from 'react'
import { explain } from '@/shared/api/problem'
import type { CursorPage } from '@/shared/api/types'

/**
 * Drives a keyset-paginated endpoint.
 *
 * Cursors point forward only, so going back means remembering where each page
 * started rather than computing it. `stack` holds the cursor that produced every
 * page visited so far; index 0 is `undefined` — the first page has no cursor.
 *
 * Any change to `deps` (i.e. the filters) resets to page 0 and clears the stack.
 * Not resetting is the classic cursor bug: a cursor minted under one filter set
 * is meaningless under another, and the backend would either error or silently
 * return the wrong window.
 */
export function useCursorPage<T>(
  fetcher: (cursor: string | undefined) => Promise<CursorPage<T>>,
  deps: unknown[] = [],
) {
  const [stack, setStack] = useState<(string | undefined)[]>([undefined])
  const [index, setIndex] = useState(0)
  const [data, setData] = useState<CursorPage<T> | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Guards against a slow earlier request overwriting a fast later one.
  const runId = useRef(0)

  const load = useCallback(async (cursor: string | undefined) => {
    const id = ++runId.current
    setLoading(true)
    setError(null)
    try {
      const page = await fetcher(cursor)
      if (id === runId.current) setData(page)
    } catch (err) {
      if (id === runId.current) {
        setError(explain(err))
        setData(null)
      }
    } finally {
      if (id === runId.current) setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  // Filters changed → back to the first page with a fresh stack.
  useEffect(() => {
    setStack([undefined])
    setIndex(0)
    void load(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  const next = useCallback(() => {
    const cursor = data?.nextCursor
    if (!cursor) return
    setStack((s) => [...s.slice(0, index + 1), cursor])
    setIndex((i) => i + 1)
    void load(cursor)
  }, [data, index, load])

  const back = useCallback(() => {
    if (index === 0) return
    const target = stack[index - 1]
    setIndex((i) => i - 1)
    void load(target)
  }, [index, stack, load])

  /** Re-runs the CURRENT page, not page 0 — so a row action does not lose place. */
  const reload = useCallback(() => load(stack[index]), [load, stack, index])

  return {
    rows: data?.content ?? [],
    hasMore: data?.hasMore ?? false,
    page: index,
    canGoBack: index > 0,
    loading,
    error,
    next,
    back,
    reload,
  }
}
